import { motion } from "framer-motion";
import { Target, Lightbulb, Users, Heart, Layers, FlaskConical } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";

const BOARDS = [
  { icon: Target, label: "Product Positioning" },
  { icon: Lightbulb, label: "Consumer Insights" },
  { icon: Users, label: "Doctor Engagement" },
  { icon: Heart, label: "Patient-Centric Marketing" },
  { icon: Layers, label: "Brand Strategy" },
  { icon: FlaskConical, label: "Urology Marketing" },
];

const JOURNEY = ["Patient", "Symptoms", "Awareness", "Doctor", "Diagnosis", "Treatment"];

export const ChapterTwo = () => (
  <ChapterShell
    n="02"
    id="chapter-2"
    era="2009 – 2016 · CIPLA"
    title="Learning the Market"
    quote="Marketing is not only about products. It is about understanding the journey behind the prescription."
  >
    <FadeUp className="glass inline-flex flex-wrap items-center gap-4 rounded-2xl px-6 py-4">
      <span className="font-display text-2xl font-black text-[#0A192F]">2009 – 2016</span>
      <span className="h-8 w-px bg-[#0A192F]/15" />
      <span className="font-display text-sm font-bold uppercase tracking-[0.15em] text-[#0A192F]">
        CIPLA — Pharmaceutical Product Manager
      </span>
    </FadeUp>

    <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {BOARDS.map((b, i) => (
        <motion.div
          key={b.label}
          initial={{ opacity: 0, y: 34, rotateX: -8 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: i * 0.08 }}
          whileHover={{ y: -6 }}
          className="glass flex items-center gap-4 rounded-2xl px-6 py-6"
          style={{ transformPerspective: 800 }}
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0A192F] text-[#00E5FF]">
            <b.icon size={19} />
          </span>
          <span className="font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A192F]">{b.label}</span>
        </motion.div>
      ))}
    </div>

    <FadeUp delay={0.15} className="mt-16 rounded-[2rem] border border-[#0A192F]/10 bg-white p-8 md:p-12">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h3 className="font-display text-2xl font-black tracking-tight text-[#0A192F] md:text-3xl">
          OABPLAN <span className="text-[#00BFA5]">— the patient-centric strategy</span>
        </h3>
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#0A192F]/45">www.oabplan.com</span>
      </div>
      <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center md:gap-0">
        {JOURNEY.map((step, i) => (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: -18 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.12 }}
            className="flex items-center md:flex-1"
          >
            <span
              className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] ${
                i === 0 ? "bg-[#0A192F] text-[#00E5FF]" : "bg-[#0A192F]/[0.06] text-[#0A192F]"
              }`}
            >
              {step}
            </span>
            {i < JOURNEY.length - 1 && (
              <span className="mx-2 hidden h-px flex-1 bg-gradient-to-r from-[#00BFA5] to-[#00E5FF] md:block" />
            )}
          </motion.div>
        ))}
      </div>
      <p className="mt-8 max-w-2xl text-sm leading-relaxed text-[#4A5568] md:text-base">
        Worked to increase reporting of patients to doctors' clinics — because behind every prescription is a person
        looking for answers.
      </p>
    </FadeUp>
  </ChapterShell>
);
